import { FC, useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Stack, Button } from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { AgTable } from '../../../../components/AgTable/AgTable';
import { transactionReconAtom } from '../../state/FinanceStateJotai';
import { defaultColDef } from '../../common-grid-defs/defaultColDefs';
import { FadeInGridWrapper } from '../../../../components/grid-renderers/FadeInGridWrapper';
import { MetricsReconDataModel } from '../../../../schemas/MetricsRecon.schema';
import { useExtendedExportSettings } from '../../../../components/AgTable/exportToExcelDefs';
import { EmptySection } from '../../../../components/EmptySection';
import { AddCartaSoiModal } from '../../Forms/AddCartaSoiModal';
import { useMetricsReconColDefs } from './useMetricsReconColDefs';
import { MissedDataPts } from './MissedDataPts';
import { trackEvent } from '../../../../util/datadogTracking';

export const TransactionReconGrid: FC = () => {
  const reconData = useAtomValue(transactionReconAtom);
  const { exportParams } = useExtendedExportSettings(new Set(['companyId', 'fundId']), 'Metrics Recon');
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Metrics reconciliation viewed (FD-3121).
  useEffect(() => {
    trackEvent('finance.reconciliation_run', { type: 'metrics' });
  }, []);

  // Use standard metrics columns (without SOI-specific features)
  const { columnDefs } = useMetricsReconColDefs();

  const getRowId = useCallback((params: GetRowIdParams<MetricsReconDataModel>): string => {
    // Handle unmapped entries that might not have IDs
    const companyPart = params.data?.companyId ?? params.data?.companyName ?? 'unknown';
    const fundPart = params.data?.fundId ?? params.data?.fundName ?? 'unknown';
    const reconPart = params.data?.reconConfigId ?? 'unknown';
    const rowId = `${companyPart}-${fundPart}-${reconPart}`;

    return rowId;
  }, []);

  // Empty state - no SOI data uploaded
  if (!reconData || reconData.length === 0) {
    return (
      <>
        <EmptySection
          title='No SOI Reconciliation Data'
          message='Upload Statement of Investments (SOI) files from Carta to see reconciliation details and identify discrepancies.'
          actionComponent={
            <Button
              size='medium'
              variant='contained'
              color='secondary'
              startIcon={<PublishIcon />}
              onClick={() => setShowUploadModal(true)}
            >
              Upload SOI Files
            </Button>
          }
        />
        {showUploadModal && (
          <AddCartaSoiModal open={showUploadModal} onClose={() => setShowUploadModal(false)} />
        )}
      </>
    );
  }

  // Also check if columnDefs are ready
  if (!columnDefs || columnDefs.length === 0) {
    return null;
  }

  return (
    <>
      <MissedDataPts numMissedDataPts={reconData.length} />
      <Stack height={'100%'}>
        <FadeInGridWrapper>
          <div style={{ height: '80vh', paddingTop: '0.5rem' }}>
            <AgTable
              key={'transaction-recon'}
              columnDefs={columnDefs as ColDef<MetricsReconDataModel>[]}
              rowData={reconData}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              defaultExcelExportParams={exportParams}
              defaultCsvExportParams={exportParams}
              suppressMovableColumns
              rowGroupPanelShow='never'
            />
          </div>
        </FadeInGridWrapper>
      </Stack>
    </>
  );
};
